import { STORAGE } from '../config/constants.js';

// localStorage quotas vary by browser and origin. Four MiB leaves headroom
// for preferences and browser accounting while still preserving useful image
// data in the autosave snapshot.
export const STORAGE_LIMITS = Object.freeze({
  AUTOSAVE_SERIALIZED_MAX: 4 * 1024 * 1024,
});

/**
 * Service for handling localStorage operations
 * Provides methods for saving and loading application state with error handling
 */
export class StorageService {
  constructor() {
    this.debounceTimer = null;
    this._lastSerialized = null; // Track last saved state for change detection
    this._pendingAutoSave = null;
    this._lifecycleTarget = null;
    this._pageHideHandler = null;
    // A record that could not be restored, held in the recovery key because
    // it could not move to its own (DEF-28). Nothing writes over it meanwhile.
    this._heldAutoSave = null;
  }

  /**
   * Flush pending recovery state when the page is being discarded. Keeping
   * this opt-in makes the service testable and lets the app detach cleanly.
   * @param {EventTarget} target - Usually window
   */
  attachLifecycle(target) {
    if (!target?.addEventListener) return;
    this.detachLifecycle();
    this._lifecycleTarget = target;
    this._pageHideHandler = () => this.flushAutoSave();
    target.addEventListener('pagehide', this._pageHideHandler);
  }

  /** Remove the page lifecycle hook without cancelling pending recovery. */
  detachLifecycle() {
    if (this._lifecycleTarget && this._pageHideHandler) {
      this._lifecycleTarget.removeEventListener('pagehide', this._pageHideHandler);
    }
    this._lifecycleTarget = null;
    this._pageHideHandler = null;
  }
  
  /**
   * Save data to localStorage
   * @param {string} key - Storage key
   * @param {any} data - Data to save (will be JSON stringified)
   * @returns {boolean} True if successful
   */
  save(key, data) {
    try {
      const serialized = JSON.stringify(data);
      return this._writeSerialized(key, serialized).ok;
    } catch (error) {
      console.error(`Failed to save to localStorage (${key}):`, error);
      return false;
    }
  }
  
  /**
   * Load data from localStorage
   * @param {string} key - Storage key
   * @param {any} defaultValue - Default value if key doesn't exist or parse fails
   * @returns {any} Parsed data or default value
   */
  load(key, defaultValue = null) {
    try {
      const item = localStorage.getItem(key);
      if (item === null) return defaultValue;
      return JSON.parse(item);
    } catch (error) {
      console.error(`Failed to load from localStorage (${key}):`, error);
      return defaultValue;
    }
  }
  
  /**
   * Remove item from localStorage
   * @param {string} key - Storage key
   * @returns {boolean} True if successful
   */
  remove(key) {
    if (this._isHeld(key)) return false;
    return this._removeKey(key);
  }
  
  /**
   * Check if a key exists in localStorage
   * @param {string} key - Storage key
   * @returns {boolean} True if key exists
   */
  exists(key) {
    try {
      return localStorage.getItem(key) !== null;
    } catch (error) {
      console.error(`Failed to check localStorage (${key}):`, error);
      return false;
    }
  }
  
  /**
   * Save application state (debounced with change detection)
   * @param {Object} state - Application state to save
   * @param {Function|null} onResult - Called after the actual storage write
   * @returns {{ok: boolean, pending?: boolean, unchanged?: boolean, error?: Error}}
   */
  autoSave(state, onResult = null) {
    let newSerialized;
    try {
      newSerialized = JSON.stringify(state);
      const bytes = new TextEncoder().encode(newSerialized).length;
      if (bytes > STORAGE_LIMITS.AUTOSAVE_SERIALIZED_MAX) {
        throw new Error('Autosave exceeds the 4 MB local-storage safety limit');
      }
    } catch (error) {
      console.error('Failed to prepare autosave:', error);
      return { ok: false, error };
    }

    // Skip if nothing changed and the recovery key still holds it: another
    // tab may have written over it or discarded it since (DEF-28).
    if (newSerialized === this._lastSerialized && this._readText(STORAGE.AUTOSAVE_KEY) === newSerialized) {
      // A different state may already be pending. Reverting to the last
      // durable state must cancel that stale write.
      this.cancelAutoSave();
      onResult?.({ ok: true, unchanged: true });
      return { ok: true, unchanged: true };
    }
    
    // Clear existing timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this._dropPending();
    this._pendingAutoSave = { serialized: newSerialized, onResult };

    // Set new timer
    this.debounceTimer = setTimeout(() => {
      this.flushAutoSave();
    }, STORAGE.AUTOSAVE_INTERVAL);
    return { ok: true, pending: true };
  }

  /**
   * Save application state whose images are still being stored (DEF-98),
   * debounced as `autoSave` is. `source.snapshot()` gives, at any moment, the
   * record as it may be written then: naming only images already stored, so a
   * record never names one the image store does not hold. `source.settle()`
   * is null once that record is final, else a promise that settles when it
   * may be. The debounced write waits for it, then writes what `snapshot()`
   * gives; `flushAutoSave` (the page closing) cannot wait, and writes it at
   * once, then the final record once it settles, should the page live on.
   * @param {{snapshot: Function, settle: Function}} source
   * @param {Function|null} onResult - Called after each write; `provisional`
   *   when it was written before its images were stored
   * @returns {{ok: boolean, pending: boolean}}
   */
  autoSaveDeferred(source, onResult = null) {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    const pending = { source, onResult };
    this._dropPending();
    this._pendingAutoSave = pending;
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      if (this._pendingAutoSave === pending) this._writeWhenSettled(pending);
    }, STORAGE.AUTOSAVE_INTERVAL);
    return { ok: true, pending: true };
  }

  /**
   * Write a pending debounced autosave immediately.
   * @returns {boolean} True when there was nothing pending or the write worked.
   */
  flushAutoSave() {
    if (!this._pendingAutoSave) return true;
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = null;

    const pending = this._pendingAutoSave;
    if (pending.source) {
      // The page may be closing: what is stored now is written now.
      if (!pending.source.settle()) return this._writeDeferred(pending).ok;
      const result = this._writeDeferred(pending, { provisional: true });
      this._writeWhenSettled(pending);
      return result.ok;
    }
    this._pendingAutoSave = null;
    const result = this._writeSerialized(STORAGE.AUTOSAVE_KEY, pending.serialized);
    if (result.ok) {
      this._lastSerialized = pending.serialized;
      console.debug('Auto-saved state');
    }
    pending.onResult?.(result);
    return result.ok;
  }

  /**
   * Persist a deferred state at once, replacing any pending write (DEF-98's
   * form of `saveAutoSave`): what is stored now, and, if its images are still
   * being stored, the final record once they are.
   * @param {{snapshot: Function, settle: Function}} source
   * @param {Function|null} [onResult] - Called after the final write, if one follows
   * @returns {boolean} Whether the immediate write worked
   */
  saveAutoSaveDeferred(source, onResult = null) {
    this.cancelAutoSave();
    const pending = { source, onResult: null };
    const settled = !source.settle();
    if (!settled) this._pendingAutoSave = pending;
    const result = this._writeDeferred(pending, { provisional: !settled, keepPending: !settled });
    if (settled) {
      source.release?.();
    } else if (this._pendingAutoSave === pending) {
      pending.onResult = onResult;
      this._writeWhenSettled(pending);
    }
    return result.ok;
  }

  /**
   * @private The pending write is dropped (replaced, cancelled or written
   * final): a deferred one's source lets go of what it held for it.
   */
  _dropPending(pending = this._pendingAutoSave) {
    if (pending?.source && !pending.released) {
      pending.released = true;
      pending.source.release?.();
    }
  }

  /**
   * @private Write `pending` once its source has settled, if it is still the
   * pending write then.
   */
  _writeWhenSettled(pending) {
    if (this._pendingAutoSave !== pending || pending.waiting) return;
    const settling = pending.source.settle();
    if (!settling) {
      this._writeDeferred(pending);
      return;
    }
    pending.waiting = true;
    const next = () => {
      pending.waiting = false;
      if (this._pendingAutoSave === pending) this._writeWhenSettled(pending);
    };
    Promise.resolve(settling).then(next, next);
  }

  /**
   * @private Serialise and write a deferred state as it stands; unless
   * `provisional`, it is no longer pending.
   */
  _writeDeferred(pending, { provisional = false, keepPending = provisional } = {}) {
    if (!keepPending && this._pendingAutoSave === pending) this._pendingAutoSave = null;
    const finish = (outcome) => {
      if (!keepPending) this._dropPending(pending);
      pending.onResult?.(outcome);
      return outcome;
    };
    let serialized;
    try {
      serialized = JSON.stringify(pending.source.snapshot());
      if (new TextEncoder().encode(serialized).length > STORAGE_LIMITS.AUTOSAVE_SERIALIZED_MAX) {
        throw new Error('Autosave exceeds the 4 MB local-storage safety limit');
      }
    } catch (error) {
      console.error('Failed to prepare autosave:', error);
      return finish({ ok: false, error, provisional });
    }
    if (serialized === this._lastSerialized && this._readText(STORAGE.AUTOSAVE_KEY) === serialized) {
      return finish({ ok: true, unchanged: true, provisional });
    }
    const result = { ...this._writeSerialized(STORAGE.AUTOSAVE_KEY, serialized), provisional };
    if (result.ok) {
      this._lastSerialized = serialized;
      console.debug('Auto-saved state');
    }
    return finish(result);
  }

  /**
   * Cancel a pending write so stale state cannot be written after Clear All or
   * a project replacement.
   * @returns {boolean} True if a pending write was cancelled.
   */
  cancelAutoSave() {
    const hadPending = Boolean(this._pendingAutoSave || this.debounceTimer);
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = null;
    this._dropPending();
    this._pendingAutoSave = null;
    return hadPending;
  }

  /**
   * Persist an autosave immediately, replacing any pending older snapshot.
   * @param {Object} state
   * @returns {boolean}
   */
  saveAutoSave(state) {
    this.cancelAutoSave();
    let serialized;
    try {
      serialized = JSON.stringify(state);
      if (new TextEncoder().encode(serialized).length > STORAGE_LIMITS.AUTOSAVE_SERIALIZED_MAX) {
        throw new Error('Autosave exceeds the 4 MB local-storage safety limit');
      }
    } catch (error) {
      console.error('Failed to prepare autosave:', error);
      return false;
    }
    const result = this._writeSerialized(STORAGE.AUTOSAVE_KEY, serialized);
    if (result.ok) {
      this._lastSerialized = serialized;
      console.debug('Auto-saved state');
    }
    return result.ok;
  }
  
  /**
   * Load auto-saved application state
   * @returns {Object|null} Saved state or null
   */
  loadAutoSave() {
    return this.load(STORAGE.AUTOSAVE_KEY, null);
  }
  
  /**
   * Clear auto-saved state
   * @returns {boolean} True if successful
   */
  clearAutoSave() {
    this.cancelAutoSave();
    const removed = this.remove(STORAGE.AUTOSAVE_KEY);
    if (removed) this._lastSerialized = null;
    return removed;
  }

  /**
   * The recovery record exactly as stored. A record that cannot be restored
   * is kept byte for byte (DEF-28), so the restore reads it raw, once, and
   * parses that same text.
   * @returns {string|null} The stored text, or null
   */
  loadAutoSaveText() {
    return this._readText(STORAGE.AUTOSAVE_KEY);
  }

  /**
   * Keep a recovery record that could not be restored until the author
   * chooses (DEF-28). A copy goes under a key of its own, which autosave never
   * writes, and only then does the original go, so new work is still saved; a
   * record kept already is not copied again. Where no copy can be written,
   * nothing is removed to make room: the record stays in the recovery key,
   * held, and a mark tells every tab of this app, and later starts, to leave
   * it until the author discards it. Tabs open at once share no lock
   * (`localStorage` has none), so the mark narrows, and cannot close, the
   * window in which another tab's cached view writes over it.
   * @param {string} text - The record exactly as the restore read it
   * @returns {{ where: 'parked', key: string, existing: boolean }
   *   | { where: 'held', durable: boolean } | { where: 'unkept' }} Where it is
   *   now: under its own key (one that held it already, if `existing`); held
   *   in the recovery key, `durable` when the key was read to hold it and its
   *   mark was written, so other tabs and later starts know; or in neither
   *   (no copy could be written, and the recovery key holds another record)
   */
  keepUnrestored(text) {
    const existingKey = this.listKept().records.find(record => record.text === text)?.key;
    const key = existingKey ?? this._copyToKept(text);
    if (key) {
      // A copy left in the recovery key would be tried, and kept, again.
      this._removeIfHolding(STORAGE.AUTOSAVE_KEY, text);
      this._releaseHold(text);
      return { where: 'parked', key, existing: Boolean(existingKey) };
    }
    const recovery = this._readKey(STORAGE.AUTOSAVE_KEY);
    if (recovery.ok && recovery.value !== text) return { where: 'unkept' };
    // A key that cannot be read may still hold it, so this tab holds it all
    // the same; but its mark goes only on a key read to hold it, since one
    // that cannot be read may hold another tab's held record, whose mark
    // must stay.
    this._heldAutoSave = text;
    const marked = recovery.ok && this._setKey(STORAGE.HELD_AUTOSAVE_KEY, fingerprint(text));
    return { where: 'held', durable: marked };
  }

  /**
   * Whether the recovery key holds a record kept because it could not be
   * restored (DEF-28), read from the store each time, so a hold another tab
   * ended, or moved, ends here too.
   * @returns {{ state: 'none' }
   *   | { state: 'held', text: string, durable: boolean }
   *   | { state: 'unknown', text: string|null }} `durable` when its mark is
   *   written; `unknown` when the mark or the key cannot be read: nothing may
   *   then write the key or restore it
   */
  holdState() {
    const mark = this._readKey(STORAGE.HELD_AUTOSAVE_KEY);
    if (mark.ok && mark.value === null && this._heldAutoSave === null) return { state: 'none' };
    const recovery = this._readKey(STORAGE.AUTOSAVE_KEY);
    if (!mark.ok || !recovery.ok) {
      // What the key may hold: what it reads, or what this tab holds there.
      return { state: 'unknown', text: recovery.ok ? recovery.value : this._heldAutoSave };
    }
    const local = this._heldAutoSave;
    if (local !== null) {
      if (recovery.value === local) {
        const durable = mark.value === fingerprint(local);
        // Held without its mark, it may since have been kept under a key of
        // its own by a tab that could not remove this copy: safe there, this
        // copy no longer stops a write.
        if (durable || !this._isKept(local)) return { state: 'held', text: local, durable };
      }
      // It has gone, or moved: another tab chose, or kept it after all.
      this._heldAutoSave = null;
      this._lastSerialized = null;
    }
    if (mark.value === null) return { state: 'none' };
    if (recovery.value !== null && mark.value === fingerprint(recovery.value)) {
      return { state: 'held', text: recovery.value, durable: true };
    }
    // A mark whose record has gone: nothing is held.
    this._removeKey(STORAGE.HELD_AUTOSAVE_KEY);
    return { state: 'none' };
  }

  /**
   * Every record kept under a key of its own, newest first (DEF-28). A key
   * that cannot be read is listed with no text: it can still be removed.
   * @returns {{ ok: boolean, records: Array<{ key: string, text: string|null }> }}
   *   `ok` is false when the store could not be searched
   */
  listKept() {
    const { ok, keys } = this._keptKeys();
    return { ok, records: keys.map(key => ({ key, text: this._readText(key) })) };
  }

  /**
   * Kept records' keys in the order a search lists them, newest first
   * (DEF-28): those this tab knows, offered when the store cannot be
   * searched, come in the same order as when it can.
   * @param {Iterable<string>} keys
   * @returns {string[]}
   */
  inKeptOrder(keys) {
    return [...keys].sort(newestKeptFirst);
  }

  /**
   * One kept record, read by the key it was kept under, for when the store
   * cannot be searched (DEF-28).
   * @param {string} key
   * @returns {{ ok: boolean, text: string|null }} `text` is null when the key
   *   holds nothing; `ok` is false when it could not be read
   */
  readKept(key) {
    const { ok, value } = this._readKey(key);
    return { ok, text: value };
  }

  /**
   * Remove a kept record, for the author's Discard — one of the two ways one
   * goes (DEF-28) — from where it was kept, with every identical copy that
   * can be read, but never a different record written there since. Ending a
   * hold removes its mark only while the mark is this record's.
   * @param {{ text: string, where: string, key?: string, copies?: string[] }} kept - As offered:
   *   `key` is the key it was kept under, if it was (one this tab may have
   *   been unable to read when it offered it); `copies`, the keys the caller
   *   knows hold it
   * @returns {boolean} Whether no copy of it is left in the store that can be
   *   read, or that the caller knows of, as far as the store could be searched
   */
  discardKept({ text, key, copies = [] }) {
    // Wherever it was when it was offered, even kept only in memory: another
    // tab may have kept it since. The recovery key's copy first, and only if
    // it is this record: if it cannot go, the record stays on offer.
    if (!this._removeIfHolding(STORAGE.AUTOSAVE_KEY, text)) return false;
    this._releaseHold(text);
    // Its own key, read or not when it was offered: one that cannot be read
    // now cannot be said to be empty.
    if (key && !this._removeIfHolding(key, text)) return false;
    // Every other copy, wherever it has gone since it was offered: another tab
    // may have kept a held record under a key of its own. A search that fails
    // cannot say none is left.
    const kept = this.listKept();
    if (!kept.ok) return false;
    let left = false;
    for (const record of kept.records) {
      if (record.text === text && !this._removeKey(record.key)) return false;
      // A copy the caller knows of, which cannot be read now, is left.
      if (record.text === null && copies.includes(record.key)) left = true;
    }
    return !left;
  }

  /**
   * Clear All's part (DEF-28): remove every kept record, without reading it,
   * and a held one, and end the hold.
   * @returns {{ ok: boolean, removed: number }} `ok` is false when a record
   *   could not be found or removed, or a hold could not be read
   */
  discardAllKept() {
    const { ok: searched, keys } = this._keptKeys();
    let ok = searched;
    let removed = 0;
    for (const key of keys) {
      if (this._removeKey(key)) removed += 1;
      else ok = false;
    }
    const hold = this.holdState();
    if (hold.state === 'held') {
      if (this._removeIfHolding(STORAGE.AUTOSAVE_KEY, hold.text)) {
        this._releaseHold(hold.text);
        removed += 1;
      } else {
        ok = false;
      }
    } else if (hold.state === 'unknown') {
      ok = false;
    }
    return { ok, removed };
  }
  
  /**
   * Save user preferences
   * @param {Object} preferences - User preferences
   * @returns {boolean} True if successful
   */
  savePreferences(preferences) {
    return this.save(STORAGE.PREFERENCES_KEY, preferences);
  }
  
  /**
   * Load user preferences
   * @returns {Object} User preferences with defaults
   */
  loadPreferences() {
    return this.load(STORAGE.PREFERENCES_KEY, {
      showSplash: true,
      theme: 'light',
      animationSpeed: 1,
      autoSave: true,
      keyboardShortcuts: true,
      highContrast: false
    });
  }
  
  /**
   * Check if splash screen should be shown
   * @returns {boolean} True if splash should be shown
   */
  shouldShowSplash() {
    return !this.exists(STORAGE.SPLASH_SHOWN_KEY);
  }
  
  /**
   * Mark splash screen as shown
   */
  markSplashShown() {
    this.save(STORAGE.SPLASH_SHOWN_KEY, true);
  }
  
  /**
   * Export all data as JSON string
   * @returns {string} JSON string of all localStorage data
   */
  exportData() {
    const data = {
      autosave: this.loadAutoSave(),
      preferences: this.loadPreferences(),
      timestamp: new Date().toISOString()
    };
    return JSON.stringify(data, null, 2);
  }
  
  /**
   * Import data from JSON string
   * @param {string} jsonString - JSON string to import
   * @returns {boolean} True if successful
   */
  importData(jsonString) {
    try {
      const data = JSON.parse(jsonString);
      
      let saved = true;
      if (data.autosave) {
        saved = this.save(STORAGE.AUTOSAVE_KEY, data.autosave) && saved;
      }
      
      if (data.preferences) {
        saved = this.save(STORAGE.PREFERENCES_KEY, data.preferences) && saved;
      }
      
      return saved;
    } catch (error) {
      console.error('Failed to import data:', error);
      return false;
    }
  }
  
  /**
   * Clear all stored data
   * @returns {boolean} True if successful
   */
  clearAll() {
    try {
      this.cancelAutoSave();
      const keys = [
        STORAGE.AUTOSAVE_KEY,
        STORAGE.PREFERENCES_KEY,
        STORAGE.SPLASH_SHOWN_KEY
      ];
      
      const removed = keys.map(key => this.remove(key)).every(Boolean);
      if (removed) this._lastSerialized = null;
      return removed;
    } catch (error) {
      console.error('Failed to clear all data:', error);
      return false;
    }
  }
  
  /**
   * Get storage size estimate
   * @returns {Promise<Object>} Storage quota and usage
   */
  async getStorageInfo() {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      try {
        const estimate = await navigator.storage.estimate();
        return {
          usage: estimate.usage,
          quota: estimate.quota,
          percentage: (estimate.usage / estimate.quota) * 100
        };
      } catch (error) {
        console.error('Failed to estimate storage:', error);
      }
    }
    return null;
  }

  /** @private */
  _readText(key) {
    return this._readKey(key).value;
  }

  /** @private */
  _readKey(key) {
    try {
      return { ok: true, value: localStorage.getItem(key) };
    } catch (error) {
      console.error(`Failed to load from localStorage (${key}):`, error);
      return { ok: false, value: null };
    }
  }

  /**
   * @private Whether `key` is the recovery key while it holds a record that
   * could not be restored (DEF-28), or may: no ordinary write or removal
   * reaches it then.
   */
  _isHeld(key) {
    return key === STORAGE.AUTOSAVE_KEY && this.holdState().state !== 'none';
  }

  /** @private Whether `text` is kept under a key of its own that can be read. */
  _isKept(text) {
    return this.listKept().records.some(record => record.text === text);
  }

  /**
   * @private End this record's hold: this tab's, and the mark, but only
   * while the mark is this record's, so a stale choice cannot free another.
   */
  _releaseHold(text) {
    if (this._heldAutoSave === text) {
      this._heldAutoSave = null;
      this._lastSerialized = null;
    }
    const mark = this._readKey(STORAGE.HELD_AUTOSAVE_KEY);
    if (mark.ok && mark.value === fingerprint(text)) this._removeKey(STORAGE.HELD_AUTOSAVE_KEY);
  }

  /** @private The kept records' keys, newest first, or `ok: false` if the store could not be searched. */
  _keptKeys() {
    const keys = [];
    try {
      for (let index = 0; index < localStorage.length; index += 1) {
        const key = localStorage.key(index);
        if (key?.startsWith(STORAGE.KEPT_AUTOSAVE_PREFIX)) keys.push(key);
      }
    } catch (error) {
      console.error('Failed to search localStorage for kept sessions:', error);
      return { ok: false, keys: [] };
    }
    return { ok: true, keys: keys.sort(newestKeptFirst) };
  }

  /**
   * @private Copy `text` to a new key of its own, never one in use; the key,
   * or null.
   */
  _copyToKept(text) {
    for (let attempt = 0; attempt < 4; attempt += 1) {
      keptSequence += 1;
      const key = `${STORAGE.KEPT_AUTOSAVE_PREFIX}${Date.now()}-${String(keptSequence).padStart(6, '0')}-${Math.random().toString(36).slice(2, 12)}`;
      const taken = this._readKey(key);
      if (!taken.ok) return null;
      if (taken.value !== null) continue;
      return this._setKey(key, text) ? key : null;
    }
    return null;
  }

  /**
   * @private Remove `key` if it holds exactly `text`. True when it no longer
   * does; false when it could not be read, or could not be removed.
   */
  _removeIfHolding(key, text) {
    const stored = this._readKey(key);
    if (!stored.ok) return false;
    if (stored.value !== text) return true;
    if (!this._removeKey(key)) return false;
    if (key === STORAGE.AUTOSAVE_KEY) this._lastSerialized = null;
    return true;
  }

  /** @private A write the hold does not stop: only kept records use it. */
  _setKey(key, text) {
    try {
      localStorage.setItem(key, text);
      return true;
    } catch (error) {
      console.error(`Failed to save to localStorage (${key}):`, error);
      return false;
    }
  }

  /** @private A removal the hold does not stop. */
  _removeKey(key) {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error(`Failed to remove from localStorage (${key}):`, error);
      return false;
    }
  }

  /** @private */
  _writeSerialized(key, serialized) {
    if (this._isHeld(key)) {
      return { ok: false, error: new Error("Browser recovery holds a session that couldn't be restored") };
    }
    try {
      localStorage.setItem(key, serialized);
      return { ok: true };
    } catch (error) {
      console.error(`Failed to save to localStorage (${key}):`, error);
      return { ok: false, error };
    }
  }
}

/** Kept records made by this tab, in order, to order those made in the same millisecond. */
let keptSequence = 0;

/** Newest first: by the time a key names, then the order this tab made it. */
function newestKeptFirst(a, b) {
  const [timeA, sequenceA] = keptOrder(a);
  const [timeB, sequenceB] = keptOrder(b);
  return timeB - timeA || sequenceB - sequenceA || b.localeCompare(a);
}

function keptOrder(key) {
  const [time, sequence] = key.slice(STORAGE.KEPT_AUTOSAVE_PREFIX.length).split('-');
  return [Number.parseInt(time, 10) || 0, Number.parseInt(sequence, 10) || 0];
}

/**
 * A held record's mark: its length and two hashes of its text (cyrb53 and
 * FNV-1a, 85 bits), enough to tell it from whatever autosave writes later
 * (DEF-28).
 * @param {string} text
 * @returns {string}
 */
function fingerprint(text) {
  let fnv = 0x811c9dc5;
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let index = 0; index < text.length; index += 1) {
    const code = text.charCodeAt(index);
    fnv = Math.imul(fnv ^ code, 0x01000193);
    h1 = Math.imul(h1 ^ code, 2654435761);
    h2 = Math.imul(h2 ^ code, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  const cyrb53 = 4294967296 * (2097151 & h2) + (h1 >>> 0);
  return `${text.length}:${cyrb53.toString(36)}:${(fnv >>> 0).toString(36)}`;
}
